import api from './client';
import { preview } from './preview';
import { readGuestToken } from './guestToken.js';

class DownloadsAPI {
  /**
   * The current report as a PDF.
   *
   * ⚠️ A DOWNLOAD IS ALWAYS THE CURRENT DOCUMENT.
   *
   * The gateway keeps a vaulted copy of the last PDF it produced, and that
   * vault is WRITE-ONCE — nothing invalidates it when the report changes. It
   * has a `?fresh` escape hatch, described in the gateway as "the report's
   * Download button", except the Download button never sent it. So a customer
   * who regenerated their plan, or whose reviewer corrected it, kept
   * downloading the first PDF ever made for that project. Indefinitely.
   *
   * Fresh is therefore the DEFAULT here rather than a flag one call site
   * remembers to pass — this client is the single door every download in the
   * app goes through, and the next caller added would otherwise inherit the
   * bug. A caller that genuinely wants the cached copy asks for it.
   *
   * The real fix is a cache keyed on the report's content hash rather than on
   * the project; until that exists, re-rendering is the only honest answer.
   */
  static async downloadPdf(projectId, options = {}) {
    const isGuest = Boolean(options?.guest);
    const guestToken = isGuest ? readGuestToken() : null;
    const base = isGuest ? `/guest/downloads/${projectId}/pdf` : `/downloads/${projectId}/pdf`;
    const path = options?.cached ? base : `${base}?fresh=1`;
    const request = () => api.get(path, {
      responseType: 'blob',
      headers: guestToken ? { Authorization: `Bearer ${guestToken}` } : undefined
    });
    try {
      const response = await request();
      return response.data;
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      // For member flow, recover from missing generated document by generating preview first.
      if (!isGuest && status === 404) {
        await preview.generate({ projectId });
        const retry = await request();
        return retry.data;
      }
      throw error;
    }
  }

  static async requirementsPdf(docId, payload = {}) {
    const response = await api.post(`/downloads/requirements/${docId}/pdf`, payload);
    return response.data.data;
  }

  static async billingInvoice(invoiceId, payload = {}) {
    const response = await api.post(`/downloads/billing/invoice/${invoiceId}`, payload);
    return response.data.data;
  }

  static async status(jobId) {
    const response = await api.get(`/downloads/status/${jobId}`);
    return response.data.data;
  }

  static async link(jobId) {
    const response = await api.get(`/downloads/link/${jobId}`);
    return response.data.data;
  }

  static async history(params = {}) {
    const response = await api.get('/downloads/my-history', { params });
    return response.data.data;
  }

  static async marketplace(purchaseId, payload = {}) {
    const response = await api.post(`/downloads/marketplace/${purchaseId}`, payload);
    return response.data.data;
  }

  static async subscriptionExclusive(payload = {}) {
    const response = await api.post('/downloads/subscription/exclusive', payload);
    return response.data.data;
  }

  static async orgActivity(orgId, params = {}) {
    const response = await api.get(`/downloads/org/${orgId}/activity`, { params });
    return response.data.data;
  }

  static async orgBulk(orgId, payload = {}) {
    const response = await api.post(`/downloads/org/${orgId}/bulk`, payload);
    return response.data.data;
  }

  static async patchRestrictions(projectId, payload = {}) {
    const response = await api.patch(`/downloads/restrictions/${projectId}`, payload);
    return response.data.data;
  }

  static async adminActiveLinks(params = {}) {
    const response = await api.get('/admin/downloads/active-links', { params });
    return response.data.data;
  }

  static async adminRevoke(linkId) {
    const response = await api.delete(`/admin/downloads/revoke/${linkId}`);
    return response.data.data;
  }

  static async adminStats() {
    const response = await api.get('/admin/downloads/stats');
    return response.data.data;
  }

  static async adminInspect(jobId, payload = {}) {
    const response = await api.post(`/admin/downloads/inspect/${jobId}`, payload);
    return response.data.data;
  }

  static async sysStorageProvider(payload = {}) {
    const response = await api.patch('/sys/downloads/config/storage-provider', payload);
    return response.data.data;
  }

  static async sysExpiration(payload = {}) {
    const response = await api.patch('/sys/downloads/config/expiration', payload);
    return response.data.data;
  }

  static async sysLimits(payload = {}) {
    const response = await api.patch('/sys/downloads/config/limits', payload);
    return response.data.data;
  }

  static async sysCleanupTemp(payload = {}) {
    const response = await api.post('/sys/downloads/cleanup/temp', payload);
    return response.data.data;
  }

  static async sysSeedTestBundles(payload = {}) {
    const response = await api.post('/sys/downloads/seed/test-bundles', payload);
    return response.data.data;
  }

  static async internalPackageTrigger(payload = {}) {
    const response = await api.post('/internal/downloads/package-trigger', payload);
    return response.data.data;
  }

  static async internalVerifyIntegrity(fileId) {
    const response = await api.get(`/internal/downloads/verify-integrity/${fileId}`);
    return response.data.data;
  }

  static async internalSyncUsage(payload = {}) {
    const response = await api.post('/internal/downloads/sync-usage', payload);
    return response.data.data;
  }
}

export { DownloadsAPI as downloads };
